import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Marquee from "react-fast-marquee";
import { Upload, Type, Image, ArrowRight, ArrowLeft, AlertTriangle, Check, Move, Maximize2, X, Plus, RotateCw, Scissors, ShoppingCart } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function resolveImg(url) {
  if (!url) return "";
  return url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
}

const ENGRAVING_FONTS = [
  { value: "Outfit", label: "Outfit", style: "Sans-Serif" },
  { value: "Roboto Slab", label: "Roboto Slab", style: "Serif" },
  { value: "Playfair Display", label: "Playfair Display", style: "Elegant Serif" },
  { value: "Oswald", label: "Oswald", style: "Bold Condensed" },
  { value: "Space Mono", label: "Space Mono", style: "Monospace" },
  { value: "Dancing Script", label: "Dancing Script", style: "Script" },
  { value: "Bebas Neue", label: "Bebas Neue", style: "Display" },
  { value: "Permanent Marker", label: "Permanent Marker", style: "Handwritten" },
];

const TEXT_SIZE_PRESETS = [
  { value: 12, label: "XS" }, { value: 16, label: "S" }, { value: 22, label: "M" },
  { value: 30, label: "L" }, { value: 40, label: "XL" }, { value: 54, label: "XXL" },
];

// Draggable overlay element
function DraggableOverlay({ children, isText, initX, initY, initW, initH, onPositionChange }) {
  const [pos, setPos] = useState({ x: initX || 30, y: initY || 30 });
  const [size, setSize] = useState({ w: initW || 120, h: initH || 80 });
  const [rotation, setRotation] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [resizing, setResizing] = useState(false);
  const [pinching, setPinching] = useState(false);
  const dragStart = useRef({ x: 0, y: 0, px: 0, py: 0 });
  const resizeStart = useRef({ x: 0, y: 0, w: 0, h: 0 });
  const pinchStart = useRef({ dist: 0, angle: 0, w: 0, h: 0, rot: 0 });
  const elRef = useRef(null);

  const getDistance = (t1, t2) => Math.sqrt((t2.clientX - t1.clientX) ** 2 + (t2.clientY - t1.clientY) ** 2);
  const getAngle = (t1, t2) => Math.atan2(t2.clientY - t1.clientY, t2.clientX - t1.clientX) * (180 / Math.PI);

  const onDragStart = useCallback((e) => {
    if (e.target.classList.contains("resize-handle") || e.target.classList.contains("rotate-handle")) return;
    // Two-finger gesture: pinch/rotate
    if (e.touches && e.touches.length === 2) {
      e.preventDefault(); e.stopPropagation();
      setPinching(true); setDragging(false);
      pinchStart.current = { dist: getDistance(e.touches[0], e.touches[1]), angle: getAngle(e.touches[0], e.touches[1]), w: size.w, h: size.h, rot: rotation };
      return;
    }
    e.preventDefault(); e.stopPropagation();
    setDragging(true);
    const t = e.touches ? e.touches[0] : e;
    dragStart.current = { x: t.clientX, y: t.clientY, px: pos.x, py: pos.y };
  }, [pos, size, rotation]);

  const onResizeStart = useCallback((e) => {
    e.preventDefault(); e.stopPropagation();
    setResizing(true);
    const t = e.touches ? e.touches[0] : e;
    resizeStart.current = { x: t.clientX, y: t.clientY, w: size.w, h: size.h };
  }, [size]);

  const handleRotate = (e) => {
    e.preventDefault(); e.stopPropagation();
    setRotation(prev => (prev + 15) % 360);
  };

  useEffect(() => {
    const handleMove = (e) => {
      // Two-finger pinch/rotate
      if (pinching && e.touches && e.touches.length === 2) {
        e.preventDefault();
        const newDist = getDistance(e.touches[0], e.touches[1]);
        const newAngle = getAngle(e.touches[0], e.touches[1]);
        const scale = newDist / (pinchStart.current.dist || 1);
        const angleDelta = newAngle - pinchStart.current.angle;
        setSize({ w: Math.max(40, pinchStart.current.w * scale), h: Math.max(30, pinchStart.current.h * scale) });
        setRotation(Math.round(pinchStart.current.rot + angleDelta));
        return;
      }
      const t = e.touches ? e.touches[0] : e;
      if (dragging) {
        setPos({ x: dragStart.current.px + (t.clientX - dragStart.current.x), y: dragStart.current.py + (t.clientY - dragStart.current.y) });
      }
      if (resizing) {
        setSize({ w: Math.max(40, resizeStart.current.w + (t.clientX - resizeStart.current.x)), h: Math.max(30, resizeStart.current.h + (t.clientY - resizeStart.current.y)) });
      }
    };
    const handleUp = () => {
      if (dragging || resizing || pinching) { setDragging(false); setResizing(false); setPinching(false); if (onPositionChange) onPositionChange(pos, size, rotation); }
    };
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    window.addEventListener("touchmove", handleMove, { passive: false });
    window.addEventListener("touchend", handleUp);
    return () => { window.removeEventListener("mousemove", handleMove); window.removeEventListener("mouseup", handleUp); window.removeEventListener("touchmove", handleMove); window.removeEventListener("touchend", handleUp); };
  }, [dragging, resizing, pinching, pos, size, rotation, onPositionChange]);

  useEffect(() => {
    if (!dragging && !resizing && !pinching && onPositionChange) onPositionChange(pos, size, rotation);
  }, [pos, size, rotation]);

  const borderClass = isText ? "border-[#FF3366]" : "border-[#00E5FF]";
  const bgClass = isText ? "bg-[#FF3366]" : "bg-[#00E5FF]";

  return (
    <div ref={elRef} className={`absolute border-2 border-dashed ${borderClass} cursor-move`} style={{ left: pos.x, top: pos.y, width: size.w, height: size.h, zIndex: dragging ? 20 : 10, transform: `rotate(${rotation}deg)`, transformOrigin: "center center" }}
      onMouseDown={onDragStart} onTouchStart={onDragStart}>
      {children}
      <div className={`resize-handle absolute bottom-0 right-0 w-5 h-5 ${bgClass} cursor-se-resize border-l-2 border-t-2 border-white`} onMouseDown={onResizeStart} onTouchStart={onResizeStart}>
        <Maximize2 size={10} className="text-white m-0.5" strokeWidth={3} />
      </div>
      <div className={`rotate-handle absolute top-0 right-0 w-5 h-5 ${bgClass} cursor-pointer border-l-2 border-b-2 border-white flex items-center justify-center`}
        onMouseDown={handleRotate} onTouchStart={handleRotate} data-testid={`rotate-${isText ? "text" : "image"}`}>
        <RotateCw size={10} className="text-white" strokeWidth={3} />
      </div>
      <div className={`absolute top-0 left-0 ${bgClass} text-white text-[7px] px-1 font-mono flex items-center gap-0.5`}>
        <Move size={7} strokeWidth={3} /> {isText ? "text" : "image"} {rotation > 0 && `${rotation}°`}
      </div>
    </div>
  );
}

// Multi-overlay canvas
function EngravingCanvas({ productImage, overlayImage, overlayText, textFont, textSize, onSaveTextPos, onSaveImagePos, label }) {
  const containerRef = useRef(null);
  const hasText = !!overlayText;
  const hasImage = !!overlayImage;
  const hasAny = hasText || hasImage;

  const normalize = (pos, sz, rot) => {
    const c = containerRef.current?.getBoundingClientRect();
    if (!c) return null;
    return { x: pos.x / c.width, y: pos.y / c.height, w: sz.w / c.width, h: sz.h / c.height, rotation: rot || 0 };
  };

  return (
    <div className="flex-1 flex flex-col">
      {label && <span className="font-heading font-bold text-xs uppercase tracking-wider text-zinc-500 mb-2">{label}</span>}
      <div ref={containerRef} className="relative border-2 border-black bg-white flex-1 min-h-[250px] max-h-[350px] overflow-hidden select-none" data-testid={`canvas-${label ? label.toLowerCase().replace(/\s/g,'-') : 'main'}`}>
        {productImage && <img src={resolveImg(productImage)} alt="Product" className="absolute inset-0 w-full h-full object-contain" draggable={false} />}

        {/* Text overlay */}
        {hasText && (
          <DraggableOverlay isText={true} initX={30} initY={30} initW={140} initH={60}
            onPositionChange={(p, s, r) => { const n = normalize(p, s, r); if (n && onSaveTextPos) onSaveTextPos(n); }}>
            <div className="w-full h-full flex items-center justify-center bg-white/60 backdrop-blur-sm p-1 overflow-hidden">
              <p className="font-bold text-center leading-tight" style={{ fontFamily: textFont || "'Outfit', sans-serif", fontSize: textSize ? `${textSize}px` : "16px" }}>
                {overlayText}
              </p>
            </div>
          </DraggableOverlay>
        )}

        {/* Image overlay */}
        {hasImage && (
          <DraggableOverlay isText={false} initX={50} initY={80} initW={120} initH={100}
            onPositionChange={(p, s, r) => { const n = normalize(p, s, r); if (n && onSaveImagePos) onSaveImagePos(n); }}>
            <img src={overlayImage} alt="Engraving" className="w-full h-full object-contain opacity-80" draggable={false} />
          </DraggableOverlay>
        )}

        {!hasAny && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/30">
            <span className="font-heading font-bold text-xs text-zinc-400 uppercase tracking-widest">Add a design below</span>
          </div>
        )}
      </div>
      {hasAny && (
        <div className="flex gap-2 mt-2">
          {hasText && <Badge className="bg-[#FF3366] text-white border-0 rounded-none font-mono text-[8px]">TEXT</Badge>}
          {hasImage && <Badge className="bg-[#00E5FF] text-black border-0 rounded-none font-mono text-[8px]">IMAGE</Badge>}
        </div>
      )}
    </div>
  );
}

function round2(n) { return Math.round(n * 100) / 100; }

export default function CustomizePage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [allCategories, setAllCategories] = useState([]);
  const [catalogImages, setCatalogImages] = useState([]);
  const [cutoutShapes, setCutoutShapes] = useState([]);
  const [selectedCutoutShape, setSelectedCutoutShape] = useState(null);

  // Front side
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const [showTextPanel, setShowTextPanel] = useState(false);
  const [showImagePanel, setShowImagePanel] = useState(false);
  const [uploadedPreview, setUploadedPreview] = useState(null);
  const [uploadedPath, setUploadedPath] = useState("");
  const [customText, setCustomText] = useState("");
  const [selectedCatalogImage, setSelectedCatalogImage] = useState(null);
  const [imageSource, setImageSource] = useState("upload"); // "upload" or "catalog"
  const [selectedFont, setSelectedFont] = useState("Outfit");
  const [textFontSize, setTextFontSize] = useState(22);
  const [textOverlayPos, setTextOverlayPos] = useState(null);
  const [imageOverlayPos, setImageOverlayPos] = useState(null);

  // Back side
  const [backEnabled, setBackEnabled] = useState(false);
  const [backSelectedCategoryIds, setBackSelectedCategoryIds] = useState([]);
  const [backShowTextPanel, setBackShowTextPanel] = useState(false);
  const [backShowImagePanel, setBackShowImagePanel] = useState(false);
  const [backUploadedPreview, setBackUploadedPreview] = useState(null);
  const [backUploadedPath, setBackUploadedPath] = useState("");
  const [backCustomText, setBackCustomText] = useState("");
  const [backSelectedCatalogImage, setBackSelectedCatalogImage] = useState(null);
  const [backImageSource, setBackImageSource] = useState("upload");
  const [backSelectedFont, setBackSelectedFont] = useState("Outfit");
  const [backTextFontSize, setBackTextFontSize] = useState(22);
  const [backTextOverlayPos, setBackTextOverlayPos] = useState(null);
  const [backImageOverlayPos, setBackImageOverlayPos] = useState(null);

  // Photo engraving toggle: false = "Subject Only" (Photo), true = "Subject and Background" (Premium)
  const [premiumUpload, setPremiumUpload] = useState(false);
  const [backPremiumUpload, setBackPremiumUpload] = useState(false);

  // Quantity
  const [quantity, setQuantity] = useState(1);

  // General
  const [designDescription, setDesignDescription] = useState("");
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeSide, setActiveSide] = useState("front");

  useEffect(() => { fetchData(); }, [productId]);
  const fetchData = async () => {
    try {
      const [prodRes, catRes, imgRes, shapesRes] = await Promise.all([
        axios.get(`${API}/products`), axios.get(`${API}/engraving-categories`), axios.get(`${API}/catalog-images`), axios.get(`${API}/cutout-shapes`)
      ]);
      setProduct(prodRes.data.find(p => p.id === productId));
      setAllCategories(catRes.data.filter(c => c.active));
      setCatalogImages(imgRes.data);
      setCutoutShapes(shapesRes.data);
    } catch (e) {  } finally { setLoading(false); }
  };

  const frontCategories = allCategories.filter(c => { const a = product?.allowed_engraving_ids; return (!a || a.length === 0) ? true : a.includes(c.id); });
  const backCategories = allCategories.filter(c => { const a = product?.allowed_back_engraving_ids; return (!a || a.length === 0) ? false : a.includes(c.id); });

  // Keyword-based category finders
  const findCat = (cats, keyword) => cats.find(c => c.style_type === keyword) || cats.find(c => c.name.toLowerCase().includes(keyword));

  // Auto-select engravings based on what's on the canvas
  useEffect(() => {
    if (!product || allCategories.length === 0) return;
    const autoSelect = (side) => {
      const cats = side === "front" ? frontCategories : backCategories;
      const textActive = side === "front" ? (showTextPanel && customText.trim()) : (backShowTextPanel && backCustomText.trim());
      const imgPanel = side === "front" ? showImagePanel : backShowImagePanel;
      const imgSrc = side === "front" ? imageSource : backImageSource;
      const hasUpload = side === "front" ? !!uploadedPreview : !!backUploadedPreview;
      const hasCatalog = side === "front" ? !!selectedCatalogImage : !!backSelectedCatalogImage;
      const isPremium = side === "front" ? premiumUpload : backPremiumUpload;

      const ids = [];
      // Text → "text" style_type
      if (textActive) {
        const tc = findCat(cats, "text");
        if (tc) ids.push(tc.id);
      }
      // Image panel active
      if (imgPanel) {
        if (imgSrc === "upload" && hasUpload) {
          // Uploaded image: premium toggle decides
          const pc = isPremium ? findCat(cats, "subject_and_background") : findCat(cats, "subject_only");
          if (pc) ids.push(pc.id);
        } else if (imgSrc === "catalog" && hasCatalog) {
          // Catalog image → "vector" style_type
          const vc = findCat(cats, "vector");
          if (vc) ids.push(vc.id);
        }
      }
      return ids;
    };
    setSelectedCategoryIds(autoSelect("front"));
    if (backEnabled) setBackSelectedCategoryIds(autoSelect("back"));
  }, [
    showTextPanel, customText, showImagePanel, imageSource, uploadedPreview, selectedCatalogImage, premiumUpload,
    backShowTextPanel, backCustomText, backShowImagePanel, backImageSource, backUploadedPreview, backSelectedCatalogImage, backPremiumUpload,
    backEnabled, allCategories, product
  ]);

  const handleFileUpload = async (e, side = "front") => {
    const file = e.target.files[0]; if (!file) return;
    const preview = URL.createObjectURL(file);
    if (side === "front") setUploadedPreview(preview); else setBackUploadedPreview(preview);
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await axios.post(`${API}/upload`, fd);
      if (side === "front") setUploadedPath(res.data.path); else setBackUploadedPath(res.data.path);
    } catch (e) {  } finally { setUploading(false); }
  };

  const getSelectedCategories = (side) => allCategories.filter(c => (side === "front" ? selectedCategoryIds : backSelectedCategoryIds).includes(c.id));
  const getTotalEngravingPrice = (side) => getSelectedCategories(side).reduce((sum, c) => sum + c.price * (side === "back" ? 0.5 : 1), 0);

  const getOverlayImage = (side) => {
    const src = side === "front" ? imageSource : backImageSource;
    if (src === "upload") {
      const preview = side === "front" ? uploadedPreview : backUploadedPreview;
      return preview || null;
    }
    const cat = side === "front" ? selectedCatalogImage : backSelectedCatalogImage;
    return cat ? `${API}/files/${cat.storage_path}` : null;
  };

  const hasText = (side) => side === "front" ? (showTextPanel && customText.trim()) : (backShowTextPanel && backCustomText.trim());
  const hasImage = (side) => side === "front" ? (showImagePanel && getOverlayImage("front")) : (backShowImagePanel && getOverlayImage("back"));

  const canProceed = () => {
    if (!hasText("front") && !hasImage("front")) return false;
    if (backEnabled) {
      if (!hasText("back") && !hasImage("back")) return false;
    }
    return true;
  };

  const getCustomizationType = (side) => {
    const t = side === "front" ? hasText("front") : hasText("back");
    const i = side === "front" ? hasImage("front") : hasImage("back");
    if (t && i) return "multi";
    if (t) return "text";
    if (i) return side === "front" ? (imageSource === "catalog" ? "catalog" : "upload") : (backImageSource === "catalog" ? "catalog" : "upload");
    return "";
  };

  const handleProceed = () => {
    const orderData = buildOrderData();
    sessionStorage.setItem("order_data", JSON.stringify(orderData));
    sessionStorage.setItem("checkout_mode", "single");
    navigate("/shipping");
  };

  const handleAddToCart = () => {
    const orderData = buildOrderData();
    const existing = JSON.parse(sessionStorage.getItem("cart_items") || "[]");
    existing.push(orderData);
    sessionStorage.setItem("cart_items", JSON.stringify(existing));
    navigate("/cart");
  };

  const buildOrderData = () => {
    const frontTotal = getTotalEngravingPrice("front");
    const backTotal = backEnabled ? getTotalEngravingPrice("back") : 0;
    return {
      product_id: productId, product_name: product.name, product_price: product.price,
      product_image: product.preview_image_url || product.image_url,
      quantity: quantity,
      engraving_category_ids: selectedCategoryIds,
      front_engravings: getSelectedCategories("front").map(c => ({ id: c.id, name: c.name, price: c.price })),
      front_engraving_total: frontTotal,
      customization_type: getCustomizationType("front"),
      customization_text: customText, customization_font: selectedFont, customization_font_size: textFontSize,
      customization_image_path: uploadedPath || (selectedCatalogImage?.storage_path ? selectedCatalogImage.storage_path : ""),
      catalog_image_id: selectedCatalogImage?.id || "",
      overlay_position: textOverlayPos || imageOverlayPos,
      text_overlay_position: textOverlayPos,
      image_overlay_position: imageOverlayPos,
      design_description: designDescription,
      back_engraving_category_ids: backEnabled ? backSelectedCategoryIds : [],
      back_engravings: backEnabled ? getSelectedCategories("back").map(c => ({ id: c.id, name: c.name, price: round2(c.price * 0.5) })) : [],
      back_engraving_total: backTotal,
      back_customization_type: backEnabled ? getCustomizationType("back") : "",
      back_customization_text: backEnabled ? backCustomText : "",
      back_customization_font: backEnabled ? backSelectedFont : "",
      back_customization_font_size: backEnabled ? backTextFontSize : 0,
      back_customization_image_path: backEnabled ? (backUploadedPath || (backSelectedCatalogImage?.storage_path ? backSelectedCatalogImage.storage_path : "")) : "",
      back_catalog_image_id: backEnabled ? (backSelectedCatalogImage?.id || "") : "",
      back_overlay_position: backEnabled ? (backTextOverlayPos || backImageOverlayPos) : null,
      back_text_overlay_position: backEnabled ? backTextOverlayPos : null,
      back_image_overlay_position: backEnabled ? backImageOverlayPos : null,
      cutout_shape_id: selectedCutoutShape?.id || "",
      cutout_shape_name: selectedCutoutShape?.name || "",
    };
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-pulse text-zinc-400 font-heading text-lg uppercase tracking-widest">Loading...</div></div>;
  if (!product) return <div className="min-h-screen flex items-center justify-center"><div className="brutal-card p-12 text-center"><AlertTriangle size={32} className="mx-auto mb-4 text-[#FF3366]" /><h2 className="font-heading text-xl font-bold uppercase mb-2">Product Not Found</h2><button onClick={() => navigate("/")} className="brutal-btn text-sm mt-4">Back to Shop</button></div></div>;

  const rearAvailable = product.rear_engraving_enabled && backCategories.length > 0;

  // Current side state helpers
  const isBack = activeSide === "back";
  const sText = isBack ? backCustomText : customText;
  const sShowText = isBack ? backShowTextPanel : showTextPanel;
  const sShowImage = isBack ? backShowImagePanel : showImagePanel;
  const sFont = isBack ? backSelectedFont : selectedFont;
  const sFontSize = isBack ? backTextFontSize : textFontSize;
  const sImgSrc = isBack ? backImageSource : imageSource;
  const sUploadPreview = isBack ? backUploadedPreview : uploadedPreview;
  const sCatalogImg = isBack ? backSelectedCatalogImage : selectedCatalogImage;

  return (
    <div className="min-h-screen" data-testid="customize-page">
      <div className="warning-banner" data-testid="customize-copyright-warning">
        <Marquee speed={40} gradient={false}><span className="mx-8 text-sm tracking-widest">WARNING: No trademarked or copyrighted images can be processed &bull;&nbsp;WARNING: No trademarked or copyrighted images can be processed &bull;&nbsp;</span></Marquee>
      </div>
      <div className="border-b-2 border-black bg-white px-6 py-4 flex items-center justify-between">
        <button onClick={() => navigate("/")} className="flex items-center gap-2 font-heading font-bold text-sm uppercase tracking-wider hover:text-[#FF3366] transition-colors" data-testid="back-to-products-btn"><ArrowLeft size={18} strokeWidth={2.5} /> Back</button>
        <span className="font-heading font-bold text-sm uppercase tracking-wider">Customize Your Product</span>
      </div>

      <div className="flex flex-col lg:flex-row min-h-[calc(100vh-120px)]">
        {/* Left: Canvas */}
        <div className="lg:w-1/2 lg:sticky lg:top-0 lg:h-screen border-b-2 lg:border-b-0 lg:border-r-2 border-black bg-zinc-50 p-8 flex flex-col">
          <div className="w-full max-w-lg mx-auto flex-1 flex flex-col">
            <div className="brutal-card p-4 mb-4 flex items-center gap-4" data-testid="product-info-bar">
              <div className="w-12 h-12 border-2 border-black bg-zinc-100 flex-shrink-0 overflow-hidden">
                {product.image_url && <img src={resolveImg(product.image_url)} alt={product.name} className="w-full h-full object-contain" />}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-heading font-bold text-sm uppercase truncate">{product.name}</h3>
                <span className="font-heading font-black text-[#FF3366]">${product.price.toFixed(2)}</span>
              </div>
              <div className="text-right">
                <span className="text-[10px] text-zinc-400 font-mono uppercase block">Engraving</span>
                <span className="font-heading font-bold text-xs">+${(getTotalEngravingPrice("front") + (backEnabled ? getTotalEngravingPrice("back") : 0)).toFixed(2)}</span>
              </div>
            </div>

            {backEnabled && (
              <div className="flex mb-3 border-2 border-black" data-testid="side-toggle">
                <button onClick={() => setActiveSide("front")} className={`flex-1 py-2 font-heading font-bold text-xs uppercase tracking-wider transition-colors ${activeSide === "front" ? "bg-[#FF3366] text-white" : "bg-white"}`} data-testid="toggle-front">Front Side</button>
                <button onClick={() => setActiveSide("back")} className={`flex-1 py-2 font-heading font-bold text-xs uppercase tracking-wider border-l-2 border-black transition-colors ${activeSide === "back" ? "bg-[#00E5FF] text-black" : "bg-white"}`} data-testid="toggle-back">Back Side</button>
              </div>
            )}

            {activeSide === "front" && (
              <EngravingCanvas
                productImage={product.preview_image_url || product.image_url}
                overlayImage={hasImage("front") ? getOverlayImage("front") : null}
                overlayText={hasText("front") ? customText : ""}
                textFont={selectedFont} textSize={textFontSize}
                onSaveTextPos={setTextOverlayPos} onSaveImagePos={setImageOverlayPos}
                label="Front Side"
              />
            )}
            {activeSide === "back" && backEnabled && (
              <EngravingCanvas
                productImage={product.preview_image_url || product.image_url}
                overlayImage={hasImage("back") ? getOverlayImage("back") : null}
                overlayText={hasText("back") ? backCustomText : ""}
                textFont={backSelectedFont} textSize={backTextFontSize}
                onSaveTextPos={setBackTextOverlayPos} onSaveImagePos={setBackImageOverlayPos}
                label="Back Side"
              />
            )}
          </div>
        </div>

        {/* Right: Tools */}
        <div className="lg:w-1/2 p-8 md:p-12 overflow-y-auto">
          {backEnabled && (
            <div className="flex gap-3 mb-6">
              <button onClick={() => setActiveSide("front")} className={`brutal-btn text-xs ${activeSide === "front" ? "" : "brutal-btn-outline"}`} data-testid="tools-front-btn">Front Side</button>
              <button onClick={() => setActiveSide("back")} className={`brutal-btn text-xs ${activeSide === "back" ? "!bg-[#00E5FF] !text-black" : "brutal-btn-outline"}`} data-testid="tools-back-btn">Back Side</button>
            </div>
          )}

          {/* Design Section — Text & Image can both be active */}
          <div className="mb-8" data-testid={`${activeSide}-design-section`}>
            <h3 className="font-heading text-lg font-bold uppercase mb-4">1. Add Your Design(s)</h3>
            <p className="text-xs text-zinc-500 mb-4">You can add <strong>both text and an image</strong> to your design. Each can be positioned independently on the preview.</p>

            {/* Add Design Buttons */}
            <div className="flex gap-3 mb-6 flex-wrap">
              <button onClick={() => { if (isBack) setBackShowTextPanel(p => !p); else setShowTextPanel(p => !p); }}
                className={`brutal-btn text-xs flex items-center gap-2 ${sShowText ? "!bg-[#FF3366] !text-white" : "brutal-btn-outline"}`}
                data-testid={`${activeSide}-toggle-text`}>
                {sShowText ? <Check size={14} strokeWidth={2.5} /> : <Plus size={14} strokeWidth={2.5} />}
                <Type size={14} strokeWidth={2.5} /> Text
              </button>
              <button onClick={() => { if (isBack) setBackShowImagePanel(p => !p); else setShowImagePanel(p => !p); }}
                className={`brutal-btn text-xs flex items-center gap-2 ${sShowImage ? "!bg-[#00E5FF] !text-black" : "brutal-btn-outline"}`}
                data-testid={`${activeSide}-toggle-image`}>
                {sShowImage ? <Check size={14} strokeWidth={2.5} /> : <Plus size={14} strokeWidth={2.5} />}
                <Image size={14} strokeWidth={2.5} /> Image
              </button>
            </div>

            {/* Text Panel */}
            {sShowText && (
              <div className="border-2 border-[#FF3366] p-5 mb-6" data-testid={`${activeSide}-text-panel`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="font-heading font-bold text-sm uppercase flex items-center gap-2"><Type size={14} strokeWidth={2.5} /> Text Engraving</span>
                  <button onClick={() => { if (isBack) setBackShowTextPanel(false); else setShowTextPanel(false); }} className="text-zinc-400 hover:text-red-500"><X size={16} /></button>
                </div>
                <textarea className="brutal-input min-h-[80px] resize-none mb-3" placeholder="Type your engraving text..." maxLength={200}
                  value={sText} onChange={e => { if (isBack) setBackCustomText(e.target.value); else setCustomText(e.target.value); }}
                  style={{ fontFamily: sFont }} data-testid={`${activeSide}-text-input`} />
                <p className="text-xs text-zinc-500 font-mono mb-4">{sText.length}/200</p>
                {/* Font */}
                <label className="font-heading font-bold text-xs uppercase block mb-2">Font</label>
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {ENGRAVING_FONTS.map(f => (
                    <button key={f.value} onClick={() => { if (isBack) setBackSelectedFont(f.value); else setSelectedFont(f.value); }}
                      className={`text-left p-2 border-2 border-black transition-all ${sFont === f.value ? "bg-[#FF3366] text-white shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]" : "bg-white hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]"}`}
                      data-testid={`${activeSide}-font-${f.value.replace(/\s/g, "-").toLowerCase()}`}>
                      <span className="block text-sm" style={{ fontFamily: `'${f.value}'` }}>{f.label}</span>
                      <span className={`text-[9px] font-mono uppercase ${sFont === f.value ? "text-white/70" : "text-zinc-400"}`}>{f.style}</span>
                    </button>
                  ))}
                </div>
                {/* Size */}
                <label className="font-heading font-bold text-xs uppercase block mb-2">Size</label>
                <div className="flex gap-2 mb-2 flex-wrap">
                  {TEXT_SIZE_PRESETS.map(s => (
                    <button key={s.value} onClick={() => { if (isBack) setBackTextFontSize(s.value); else setTextFontSize(s.value); }}
                      className={`px-2 py-1 border-2 border-black font-heading font-bold text-xs ${sFontSize === s.value ? "bg-[#00E5FF] text-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]" : "bg-white"}`}
                      data-testid={`${activeSide}-size-${s.label.toLowerCase()}`}>{s.label}</button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <input type="range" min="8" max="72" value={sFontSize} onChange={e => { if (isBack) setBackTextFontSize(parseInt(e.target.value)); else setTextFontSize(parseInt(e.target.value)); }}
                    className="flex-1 accent-[#FF3366] h-2" data-testid={`${activeSide}-size-slider`} />
                  <span className="font-heading font-black text-sm border-2 border-black px-2 py-0.5 min-w-[3rem] text-center" data-testid={`${activeSide}-size-value`}>{sFontSize}px</span>
                </div>
              </div>
            )}

            {/* Image Panel */}
            {sShowImage && (
              <ImagePanel
                side={activeSide}
                imageSource={sImgSrc}
                setImageSource={(v) => { if (isBack) setBackImageSource(v); else setImageSource(v); }}
                uploadedPreview={sUploadPreview}
                uploading={uploading}
                handleFileUpload={(e) => handleFileUpload(e, activeSide)}
                catalogImages={catalogImages}
                selectedCatalogImage={sCatalogImg}
                setSelectedCatalogImage={(v) => { if (isBack) setBackSelectedCatalogImage(v); else setSelectedCatalogImage(v); }}
                onClose={() => { if (isBack) setBackShowImagePanel(false); else setShowImagePanel(false); }}
                premiumUpload={isBack ? backPremiumUpload : premiumUpload}
                setPremiumUpload={(v) => { if (isBack) setBackPremiumUpload(v); else setPremiumUpload(v); }}
                allCategories={isBack ? backCategories : frontCategories}
                isBack={isBack}
              />
            )}
          </div>

          {/* Rear Engraving Toggle */}
          {rearAvailable && activeSide === "front" && (
            <div className="mb-8 border-2 border-black p-4" data-testid="rear-engraving-toggle">
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={backEnabled} onChange={e => setBackEnabled(e.target.checked)}
                  className="w-5 h-5 border-2 border-black accent-[#00E5FF]" data-testid="rear-engraving-checkbox" />
                <div><span className="font-heading font-bold text-sm uppercase block">Add Rear Side Engraving</span><span className="text-xs text-zinc-500">Back side at 50% pricing</span></div>
              </label>
            </div>
          )}

          {/* Cutout Shape Selection */}
          {product.cutout_enabled && (() => {
            const allowedIds = product.allowed_cutout_shape_ids || [];
            const availableShapes = allowedIds.length > 0 ? cutoutShapes.filter(s => allowedIds.includes(s.id)) : cutoutShapes;
            if (availableShapes.length === 0) return null;
            return (
              <div className="mb-8 border-2 border-black p-5" data-testid="cutout-section">
                <div className="flex items-center gap-2 mb-4">
                  <Scissors size={18} strokeWidth={2.5} className="text-[#FF3366]" />
                  <h3 className="font-heading font-bold text-sm uppercase">Cutout Shape (Optional)</h3>
                </div>
                <p className="text-xs text-zinc-500 mb-4">Select a cutout shape for your product. Leave unselected for no cutout.</p>
                <div className="flex gap-3 overflow-x-auto pb-2" data-testid="cutout-shapes-scroll">
                  <button onClick={() => setSelectedCutoutShape(null)}
                    className={`flex-shrink-0 w-20 h-20 border-2 border-black flex items-center justify-center font-heading font-bold text-xs uppercase transition-all ${!selectedCutoutShape ? "bg-zinc-200 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]" : "bg-white hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]"}`}
                    data-testid="cutout-none">
                    No Cut
                  </button>
                  {availableShapes.map(shape => (
                    <button key={shape.id} onClick={() => setSelectedCutoutShape(shape)}
                      className={`flex-shrink-0 w-20 h-20 border-2 border-black overflow-hidden transition-all ${selectedCutoutShape?.id === shape.id ? "shadow-[3px_3px_0px_0px_#FF3366] ring-2 ring-[#FF3366]" : "hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]"}`}
                      data-testid={`cutout-shape-${shape.id}`}>
                      <img src={`${API}/files/${shape.storage_path}`} alt={shape.name} className="w-full h-full object-contain p-1" />
                    </button>
                  ))}
                </div>
                {selectedCutoutShape && (
                  <div className="mt-3 p-3 border-2 border-[#FF3366] bg-[#FF3366]/10 flex items-center gap-2">
                    <Scissors size={14} className="text-[#FF3366]" strokeWidth={2.5} />
                    <span className="font-heading font-bold text-xs uppercase">Selected: {selectedCutoutShape.name}</span>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Design Description */}
          <div className="mb-8" data-testid="design-description-section">
            <h3 className="font-heading text-lg font-bold uppercase mb-3">2. Design Description</h3>
            <textarea className="brutal-input min-h-[80px] resize-none" placeholder="Special instructions or details..." maxLength={500}
              value={designDescription} onChange={e => setDesignDescription(e.target.value)} data-testid="design-description-input" />
            <p className="text-xs text-zinc-500 mt-1 font-mono">{designDescription.length}/500</p>
          </div>

          {/* Quantity */}
          <div className="mb-8" data-testid="quantity-section">
            <h3 className="font-heading text-lg font-bold uppercase mb-3">3. Quantity</h3>
            <div className="flex items-center gap-4">
              <button onClick={() => setQuantity(q => Math.max(1, q - 1))} className="brutal-btn brutal-btn-outline w-10 h-10 flex items-center justify-center text-lg font-bold p-0" data-testid="qty-decrease">-</button>
              <span className="font-heading font-black text-2xl w-12 text-center" data-testid="qty-display">{quantity}</span>
              <button onClick={() => setQuantity(q => q + 1)} className="brutal-btn brutal-btn-outline w-10 h-10 flex items-center justify-center text-lg font-bold p-0" data-testid="qty-increase">+</button>
            </div>
          </div>

          {/* Price Summary */}
          <div className="brutal-card p-4 mb-6" data-testid="price-summary">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span>Product</span><span className="font-bold">${product.price.toFixed(2)}</span></div>
              {getSelectedCategories("front").map(c => (<div key={c.id} className="flex justify-between"><span className="text-zinc-500">+ {c.name}</span><span>${c.price.toFixed(2)}</span></div>))}
              {backEnabled && getSelectedCategories("back").map(c => (<div key={c.id} className="flex justify-between"><span className="text-zinc-500">+ {c.name} (back)</span><span>${round2(c.price * 0.5).toFixed(2)}</span></div>))}
              {quantity > 1 && <div className="flex justify-between text-zinc-500"><span>x {quantity}</span><span></span></div>}
              <div className="border-t-2 border-black pt-2 flex justify-between font-heading font-black text-lg"><span>Total</span><span className="text-[#FF3366]">${((product.price + getTotalEngravingPrice("front") + (backEnabled ? getTotalEngravingPrice("back") : 0)) * quantity).toFixed(2)}</span></div>
            </div>
          </div>

          <div className="flex items-center justify-between gap-4 flex-wrap">
            <button onClick={() => navigate("/")} className="brutal-btn brutal-btn-outline text-sm" data-testid="cancel-btn"><ArrowLeft size={14} className="inline mr-2" /> Cancel</button>
            <div className="flex gap-3">
              <button onClick={handleAddToCart} disabled={!canProceed()} className={`brutal-btn brutal-btn-outline text-sm flex items-center gap-2 ${!canProceed() ? "opacity-50 cursor-not-allowed" : ""}`} data-testid="add-to-cart-btn">
                <ShoppingCart size={14} strokeWidth={2.5} /> Add to Cart
              </button>
              <button onClick={handleProceed} disabled={!canProceed()} className={`brutal-btn text-sm flex items-center gap-2 ${!canProceed() ? "opacity-50 cursor-not-allowed" : ""}`} data-testid="proceed-to-shipping-btn">
                Buy Now <ArrowRight size={14} strokeWidth={2.5} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Image upload/catalog panel
function ImagePanel({ side, imageSource, setImageSource, uploadedPreview, uploading, handleFileUpload, catalogImages, selectedCatalogImage, setSelectedCatalogImage, onClose, premiumUpload, setPremiumUpload, allCategories, isBack }) {
  const fileRef = useRef(null);
  const catalogScrollRef = useRef(null);
  const round2 = (n) => Math.round(n * 100) / 100;
  const findCat = (keyword) => allCategories.find(c => c.style_type === keyword) || allCategories.find(c => c.name.toLowerCase().includes(keyword));
  const photoCat = findCat("subject_only");
  const premiumCat = findCat("subject_and_background");
  const vectorCat = findCat("vector");

  const scrollCatalog = (dir) => {
    if (catalogScrollRef.current) {
      catalogScrollRef.current.scrollBy({ left: dir * 200, behavior: "smooth" });
    }
  };

  return (
    <div className="border-2 border-[#00E5FF] p-5 mb-6" data-testid={`${side}-image-panel`}>
      <div className="flex items-center justify-between mb-3">
        <span className="font-heading font-bold text-sm uppercase flex items-center gap-2"><Image size={14} strokeWidth={2.5} /> Image Engraving</span>
        <button onClick={onClose} className="text-zinc-400 hover:text-red-500"><X size={16} /></button>
      </div>

      {/* Source toggle */}
      <div className="flex mb-4 border-2 border-black">
        <button onClick={() => setImageSource("upload")} className={`flex-1 py-2 font-heading font-bold text-xs uppercase ${imageSource === "upload" ? "bg-[#00E5FF] text-black" : "bg-white"}`} data-testid={`${side}-src-upload`}>
          <Upload size={12} className="inline mr-1" /> Upload
        </button>
        <button onClick={() => setImageSource("catalog")} className={`flex-1 py-2 font-heading font-bold text-xs uppercase border-l-2 border-black ${imageSource === "catalog" ? "bg-[#00E5FF] text-black" : "bg-white"}`} data-testid={`${side}-src-catalog`}>
          <Image size={12} className="inline mr-1" /> Catalog
        </button>
      </div>

      {imageSource === "upload" ? (
        <div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileUpload} />
          <div className="border-2 border-dashed border-black p-6 text-center cursor-pointer hover:border-[#00E5FF] transition-colors" onClick={() => fileRef.current?.click()} data-testid={`${side}-upload-drop`}>
            {uploadedPreview ? <img src={uploadedPreview} alt="Preview" className="max-h-24 mx-auto object-contain" /> : (
              <><Upload size={28} className="mx-auto mb-2 text-zinc-400" /><p className="font-heading font-bold text-xs uppercase">Click to upload</p></>
            )}
          </div>
          {uploading && <p className="text-xs text-zinc-500 mt-2 animate-pulse">Uploading...</p>}
          <div className="mt-3 flex items-center gap-2 px-2 py-1 bg-[#FFD600] border-2 border-black"><AlertTriangle size={14} /><span className="text-[10px] font-heading font-bold uppercase">No trademarked/copyrighted images</span></div>

          {/* Subject Only vs Subject and Background toggle */}
          {uploadedPreview && (
            <div className="mt-4 border-2 border-black p-4" data-testid={`${side}-engraving-style-toggle`}>
              <label className="font-heading font-bold text-xs uppercase block mb-3">Engraving Style</label>
              <div className="flex border-2 border-black">
                <button
                  onClick={() => setPremiumUpload(false)}
                  className={`flex-1 py-3 px-2 font-heading font-bold text-xs uppercase transition-all ${!premiumUpload ? "bg-[#00E5FF] text-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]" : "bg-white"}`}
                  data-testid={`${side}-style-subject-only`}
                >
                  Subject Only
                  {photoCat && <span className="block font-black text-sm mt-1">+${isBack ? round2(photoCat.price * 0.5).toFixed(2) : photoCat.price.toFixed(2)}</span>}
                </button>
                <button
                  onClick={() => setPremiumUpload(true)}
                  className={`flex-1 py-3 px-2 font-heading font-bold text-xs uppercase border-l-2 border-black transition-all ${premiumUpload ? "bg-[#FF3366] text-white shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]" : "bg-white"}`}
                  data-testid={`${side}-style-subject-bg`}
                >
                  Subject & Background
                  {premiumCat && <span className="block font-black text-sm mt-1">+${isBack ? round2(premiumCat.price * 0.5).toFixed(2) : premiumCat.price.toFixed(2)}</span>}
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        catalogImages.length === 0 ? (
          <div className="border-2 border-dashed border-black p-6 text-center" data-testid={`${side}-no-catalog`}><Image size={28} className="mx-auto mb-2 text-zinc-400" /><p className="text-xs">No catalog images available</p></div>
        ) : (
          <div>
            <div className="relative" data-testid={`${side}-catalog-scroll-container`}>
              {catalogImages.length > 3 && (
                <button onClick={() => scrollCatalog(-1)} className="absolute -left-3 top-1/2 -translate-y-1/2 z-10 w-8 h-8 border-2 border-black bg-white flex items-center justify-center shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:bg-zinc-100" data-testid={`${side}-catalog-prev`}>
                  <ArrowLeft size={14} strokeWidth={2.5} />
                </button>
              )}
              <div ref={catalogScrollRef} className="flex gap-3 overflow-x-auto pb-2 scroll-smooth" data-testid={`${side}-catalog-grid`} style={{ scrollbarWidth: "thin" }}>
                {catalogImages.map(img => (
                  <button key={img.id} onClick={() => setSelectedCatalogImage(img)}
                    className={`flex-shrink-0 w-24 h-24 border-2 border-black overflow-hidden ${selectedCatalogImage?.id === img.id ? "shadow-[3px_3px_0px_0px_#00E5FF] ring-2 ring-[#00E5FF]" : "hover:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]"}`}
                    data-testid={`${side}-catalog-img-${img.id}`}>
                    <img src={`${API}/files/${img.storage_path}`} alt={img.name} className="w-full h-full object-contain p-1" />
                  </button>
                ))}
              </div>
              {catalogImages.length > 3 && (
                <button onClick={() => scrollCatalog(1)} className="absolute -right-3 top-1/2 -translate-y-1/2 z-10 w-8 h-8 border-2 border-black bg-white flex items-center justify-center shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:bg-zinc-100" data-testid={`${side}-catalog-next`}>
                  <ArrowRight size={14} strokeWidth={2.5} />
                </button>
              )}
            </div>
            {selectedCatalogImage && vectorCat && (
              <div className="mt-3 p-3 border-2 border-[#00E5FF] bg-[#00E5FF]/10">
                <span className="font-heading font-bold text-xs uppercase">Simple Vector Graphic</span>
                <span className="font-heading font-black text-sm ml-2">+${isBack ? round2(vectorCat.price * 0.5).toFixed(2) : vectorCat.price.toFixed(2)}</span>
              </div>
            )}
          </div>
        )
      )}
    </div>
  );
}
